import ms from "ms";
import { z } from "zod";
import { AdvisorId, ClientId, ServiceTeamId, } from "../../Types";
import { makeHook } from "../../utils/makeSingleArgHook";
import getTasksV4 from "../requests/getTasks";
import { tasksUrl } from "../requests/urls";
import useGetTaskResources from "./useGetTaskResources";
export function tasksKeyMakerV4(queryParams) {
    if (queryParams === null) {
        return [tasksUrl("v4")];
    }
    return [tasksUrl("v4"), JSON.stringify(queryParams)];
}
const useGetTasksV4Internal = makeHook(async (queryParams, auth) => getTasksV4(auth, queryParams), (queryParams) => tasksKeyMakerV4(queryParams), { staleTime: ms("1minute") });
const useGetTasksV4 = (queryParams, auth) => {
    const tasksQuery = useGetTasksV4Internal(queryParams, auth);
    const taskResourcesQuery = useGetTaskResources(auth);
    if (tasksQuery.isLoading || taskResourcesQuery.isLoading) {
        return {
            data: undefined,
            isLoading: true,
            hasError: false,
        };
    }
    if (tasksQuery.hasError) {
        return {
            data: undefined,
            isLoading: false,
            hasError: true,
            error: tasksQuery.error,
        };
    }
    if (taskResourcesQuery.hasError) {
        return {
            data: undefined,
            isLoading: false,
            hasError: true,
            error: taskResourcesQuery.error,
        };
    }
    const tasks = tasksQuery.data?.values ?? [];
    const { advisorsMap, clientsMap, serviceTeamsMap, taskTypesMap } = taskResourcesQuery.data ?? {};
    const enrichedTasks = tasks.map((task) => {
        const { data: advisorId } = AdvisorId.safeParse(task.fields.advisor.value);
        const { data: assigneeId } = AdvisorId.safeParse(task.fields.assignee?.value);
        const { data: clientId } = ClientId.safeParse(task.fields.client?.value);
        const { data: serviceTeamId } = ServiceTeamId.safeParse(task.fields.serviceTeamId?.value);
        const { data: taskTypeId } = z
            .string()
            .safeParse(task.fields.taskType.value);
        const advisor = advisorId ? advisorsMap.get(advisorId) : null;
        const assignee = assigneeId ? advisorsMap.get(assigneeId) : null;
        const client = clientId ? clientsMap.get(clientId) : null;
        const serviceTeam = serviceTeamId
            ? serviceTeamsMap.get(serviceTeamId)
            : null;
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        const taskType = taskTypeId ? taskTypesMap.get(taskTypeId) : null;
        const advisorDetails = advisor
            ? {
                name: `${advisor.name.first} ${advisor.name.last}`,
                avatar: advisor.details.headshotPath ?? undefined,
            }
            : null;
        const assigneeDetails = assignee
            ? {
                name: `${assignee.name.first} ${assignee.name.last}`,
                avatar: assignee.details.headshotPath ?? undefined,
            }
            : null;
        const clientDetails = client
            ? {
                name: `${client.name.first} ${client.name.last}`,
            }
            : null;
        return {
            ...task,
            fields: {
                ...task.fields,
                advisor: {
                    ...task.fields.advisor,
                    ...advisorDetails,
                },
                ...(task.fields.assignee && {
                    assignee: {
                        ...task.fields.assignee,
                        ...assigneeDetails,
                    },
                }),
                ...(task.fields.client && {
                    client: {
                        ...task.fields.client,
                        ...clientDetails,
                    },
                }),
                ...(task.fields.serviceTeamId && {
                    serviceTeamId: {
                        ...task.fields.serviceTeamId,
                        name: serviceTeam?.name,
                    },
                }),
                taskType: {
                    ...task.fields.taskType,
                    name: taskType?.displayName,
                },
            },
        };
    });
    return {
        data: {
            values: enrichedTasks,
            pagination: tasksQuery.data?.pagination,
        },
        isLoading: false,
        hasError: false,
    };
};
export default useGetTasksV4;
//# sourceMappingURL=useGetTasks.js.map